/* eslint-disable react-hooks/exhaustive-deps */
/*
  AdminOntology — Stage C admin UI.

  Two panes:
    1. Archetype validation — rolling 30-day metrics; status badges (validated /
       watch / weak / sparse). Sort: weakness first (action items).
    2. Proposal queue — emerging archetype candidates from cluster_proposer;
       approve / discard with optional name + category overrides.
*/
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, CheckCircle2, X, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  "provider", "emotional", "communication", "attachment",
  "consistency", "validation", "generosity", "control",
];

const STATUS_STYLE = {
  validated: "bg-sage/30 text-cocoa",
  weak:      "bg-rose/25 text-mauve",
  watch:     "bg-champagne text-cocoa",
  sparse:    "bg-cocoa/10 text-cocoa/55",
};

function Pct({ v }) {
  if (v === null || v === undefined) return <span className="text-cocoa/40">—</span>;
  return <span>{(v * 100).toFixed(0)}%</span>;
}

function ArchetypeValidation() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const r = await api.get("/admin/ontology/archetypes?days=30");
    setRows(r.data?.archetypes || []);
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/ontology/refresh?days=30");
      setMsg(`Aggregated ${r.data.aggregator?.count} archetypes · ${r.data.proposer?.proposals_new} new proposals · ${r.data.proposer?.proposals_refreshed} refreshed.`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Refresh failed."); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-ontology-validation" className="ss-card p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Archetype validation</p>
          <h2 className="font-heading text-2xl text-cocoa mt-1">Rolling 30-day signal.</h2>
          <p className="text-cocoa/65 text-sm mt-1">
            Each archetype's session count and feedback outcomes from the last 30 days.
            <strong> Weak</strong> = high data, low helpful rate — review the prompt or retire.
            <strong> Sparse</strong> = not enough signal yet.
          </p>
        </div>
        <button data-testid="ontology-refresh" disabled={busy} onClick={refresh}
          className="ss-pill ss-pill-primary h-10 px-5 disabled:opacity-50">
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh now
        </button>
      </div>
      {msg && <p className="mt-3 text-cocoa/70 text-sm">{msg}</p>}

      <div className="mt-5">
        <table className="w-full text-sm ss-table-stack" data-testid="ontology-table">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.3em] uppercase text-cocoa/55 border-b border-champagne">
              <th className="py-2 pr-3">Archetype</th>
              <th className="py-2 pr-3">Sessions</th>
              <th className="py-2 pr-3">Feedback</th>
              <th className="py-2 pr-3">Helpful</th>
              <th className="py-2 pr-3">Score</th>
              <th className="py-2 pr-3">Consistency↑</th>
              <th className="py-2 pr-3">Progressed</th>
              <th className="py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-testid={`ontology-row-${r.key}`} className="border-b border-champagne/40">
                <td className="py-2 pr-3 text-cocoa">{r.name}<div className="text-[10px] text-cocoa/45 uppercase tracking-wider">{r.category}</div></td>
                <td data-label="Sessions" className="py-2 pr-3 text-cocoa/80">{r.sessions || 0}</td>
                <td data-label="Feedback" className="py-2 pr-3 text-cocoa/80">{r.feedback_count || 0}</td>
                <td data-label="Helpful" className="py-2 pr-3 text-cocoa/80"><Pct v={r.helpful_rate} /></td>
                <td data-label="Score" className="py-2 pr-3 text-cocoa/80">{r.avg_score ?? "—"}</td>
                <td data-label="Consistency" className="py-2 pr-3 text-cocoa/80"><Pct v={r.outcomes?.consistency_improved} /></td>
                <td data-label="Progressed" className="py-2 pr-3 text-cocoa/80"><Pct v={r.outcomes?.progressed} /></td>
                <td data-label="Status" className="py-2 pr-3">
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || STATUS_STYLE.sparse}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ProposalCard({ p, onDecided }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [name, setName] = useState(p.suggested_name || "");
  const [category, setCategory] = useState(p.suggested_category || "emotional");
  const [note, setNote] = useState("");

  const decide = async (which) => {
    setBusy(true); setMsg("");
    try {
      const url = `/admin/ontology/proposals/${encodeURIComponent(p.id)}/${which}`;
      const payload = which === "approve"
        ? { name_override: name, category_override: category, note: note || undefined }
        : { note: note || undefined };
      await api.post(url, payload);
      setMsg(which === "approve" ? "Approved." : "Discarded.");
      onDecided && onDecided();
    } catch (e) { setMsg(e?.response?.data?.detail || `${which} failed.`); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`proposal-${p.id}`} className="ss-card p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Proposed archetype</p>
          <h3 className="font-heading text-2xl text-cocoa mt-1">{p.suggested_name}</h3>
          <p className="text-[10px] tracking-widest uppercase text-cocoa/55 mt-1">{p.suggested_category} · {p.evidence_count} signals</p>
        </div>
        <div className="flex gap-2">
          <button data-testid={`proposal-${p.id}-open`} onClick={() => setOpen(!open)}
            className="ss-pill ss-pill-ghost h-9 text-xs">{open ? "Close" : "Review"}</button>
        </div>
      </div>
      {p.description && <p className="mt-3 text-cocoa/80 text-sm leading-relaxed">{p.description}</p>}

      {open && (
        <div className="mt-4 space-y-3">
          {p.key_signals?.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Signals</p>
              <ul className="mt-1 text-sm text-cocoa/80 list-disc list-inside space-y-0.5">
                {p.key_signals.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {p.key_risks?.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Risks</p>
              <ul className="mt-1 text-sm text-cocoa/80 list-disc list-inside space-y-0.5">
                {p.key_risks.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {p.sample_excerpts?.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Evidence (anonymised)</p>
              <div className="mt-1 space-y-2">
                {p.sample_excerpts.map((e, i) => (
                  <div key={i} className="ss-card p-3 bg-blush/10 text-xs text-cocoa/75 italic">"{e}"</div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-2 pt-2 border-t border-champagne">
            <input data-testid={`proposal-${p.id}-name`} value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Final name" className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
            <select data-testid={`proposal-${p.id}-category`} value={category} onChange={(e) => setCategory(e.target.value)}
              className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional decision note"
            className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          {msg && <p className="text-cocoa/70 text-xs">{msg}</p>}
          <div className="flex gap-2 justify-end">
            <button data-testid={`proposal-${p.id}-discard`} disabled={busy} onClick={() => decide("discard")}
              className="ss-pill ss-pill-ghost h-9 text-xs disabled:opacity-50"><X size={12} /> Discard</button>
            <button data-testid={`proposal-${p.id}-approve`} disabled={busy || name.length < 2} onClick={() => decide("approve")}
              className="ss-pill ss-pill-primary h-9 text-xs disabled:opacity-50"><CheckCircle2 size={12} /> Approve & add</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProposalQueue() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const load = async () => {
    const r = await api.get(`/admin/ontology/proposals?status=${statusFilter}`);
    setItems(r.data?.proposals || []);
  };
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <section data-testid="admin-ontology-proposals" className="ss-card p-6 mt-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Emerging archetypes</p>
          <h2 className="font-heading text-2xl text-cocoa mt-1">Proposal queue.</h2>
          <p className="text-cocoa/65 text-sm mt-1">Patterns the model is seeing that don't fit any canonical archetype.</p>
        </div>
        <div className="flex gap-1">
          {["pending", "approved", "discarded", "all"].map((s) => (
            <button key={s} data-testid={`proposals-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 h-9 text-xs rounded-full ${statusFilter === s ? "bg-cocoa text-ivory" : "bg-champagne/40 text-cocoa/70"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {items.length === 0 && (
          <div className="ss-card p-5 text-cocoa/60 text-sm flex items-center gap-2">
            <AlertTriangle size={14} className="text-mauve" />
            No {statusFilter === "all" ? "" : statusFilter} proposals.
            {statusFilter === "pending" && " Click 'Refresh now' above to mine recent advisor signals."}
          </div>
        )}
        {items.map((p) => <ProposalCard key={p.id} p={p} onDecided={load} />)}
      </div>
    </section>
  );
}

export function AdminOntology() {
  return (
    <div data-testid="admin-ontology">
      <ArchetypeValidation />
      <ProposalQueue />
    </div>
  );
}
